import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CourseProgressStats,
  DashboardProgressStats,
  ProgressStats,
} from '@/models/StudentDashboard';
import { IconTypeImport } from '@/components/ui/custom/LessonIconContainer';
import { ClassNameComponent } from '@/utils/ClassNameComponent';

export type Stats = ProgressStats & {
  title: string;
  className: ClassNameComponent;
  icon: React.ReactNode;
  description: (total: number) => string;
};

export function useStudentProgressStats(statsCollection: CourseProgressStats) {
  const { lessons, activities, challenges } = statsCollection;

  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard',
  });

  const stats = useMemo(() => {
    const statsObj: {
      [key in DashboardProgressStats]: Stats;
    } = {
      lessons: {
        ...lessons,
        title: t('stats.lessons'),
        className: {
          base: 'bg-accent-content',
          icon: 'bg-accent/25',
          progress: 'text-accent',
          div: 'text-accent/20',
        },
        icon: IconTypeImport['teacherBook'],
        description: (total: number) =>
          t('description', {
            total,
            type: t('assisted'),
          }),
      },
      activities: {
        ...activities,
        title: t('stats.activities'),
        className: {
          base: 'bg-warning-content',
          icon: 'bg-warning/25',
          progress: 'text-warning',
          div: 'text-warning/20',
        },
        icon: IconTypeImport['activity'],
        description: (total: number) =>
          t('description', {
            total,
            type: t('concluded'),
          }),
      },
      challenges: {
        ...challenges,
        title: t('stats.challenges'),
        className: {
          base: 'bg-error-content',
          icon: 'bg-error/25',
          progress: 'text-error',
          div: 'text-error/20',
        },
        icon: IconTypeImport['challenge'],
        description: (total: number) =>
          t('description', {
            total,
            type: t('completed'),
          }),
      },
    };

    return statsObj;
  }, [lessons, activities, challenges, t]);

  return stats;
}
